import React, { useEffect, useState } from 'react';
import { Modal } from 'react-bootstrap';

import { CreateUpdateProjectWithTeamDto } from '../../../common/pokCore/autogenerated/pokApiClient';
import { useTranslation } from '../../../common/hooks/useTranslation';
import LastEditorInfo from '../../../app/components/LastEditorInfo';
import { usePokCore } from '../../../common/hooks/usePokCore';
import ValidationAlert from '../../../app/components/ValidationAlert';
import { useNotifications } from '../../../common/hooks/useNotifications';
import Button from '../../../common/components/Button/Button';
import { ProjectApplicationTypeEnum } from '../../../common/pokCore/validation/enums';

import { ProjectsApplicationDetailsEditForm } from './ProjectsApplicationDetailsEditForm';

interface ProjectsApplicationDetailsModalProps {
  project?: CreateUpdateProjectWithTeamDto;
  applicationType: ProjectApplicationTypeEnum;
  purchaserName: string;
  brandName: string;
  onAccept: () => Promise<{
    saved: boolean;
    errors: string[];
  }>;
  onReject: (rejectedNote?: string) => Promise<{
    saved: boolean;
    errors: string[];
  }>;
  onClose: () => void;
  show?: boolean;
  readOnly?: boolean;
  rejectedNote?: string;
}

export const ProjectsApplicationDetailsModal: React.FC<
  ProjectsApplicationDetailsModalProps
> = props => {
  const { t, tk } = useTranslation('applicationLng');
  const statusOptions = [
    { value: 'approved', label: t(tk.app.approved) },
    { value: 'rejected', label: t(tk.app.rejected) },
  ];
  const [status, setStatus] = useState(statusOptions[0]);
  const [rejectedNote, setRejectedNote] = useState<string | undefined>();
  const [saveErrors, setSaveErrors] = useState<string[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const pok = usePokCore();
  const notifications = useNotifications();

  useEffect(() => {
    setStatus(statusOptions[0]);
    setSaveErrors([]);
    setRejectedNote(undefined);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.project?.id]);

  const handleClick = async () => {
    setIsLoading(true);
    const { saved, errors } = await (status.value === statusOptions[0].value
      ? props.onAccept()
      : props.onReject(rejectedNote));
    if (saved) {
      notifications.saveCompleted();
    } else {
      setSaveErrors(errors);
    }
    setIsLoading(false);
  };

  const isStatusReversible =
    !props.project?.active &&
    (props.applicationType === ProjectApplicationTypeEnum.Delayed
      ? props.rejectedNote !== undefined
      : props.applicationType === ProjectApplicationTypeEnum.DateChange
        ? false
        : true);

  return (
    <Modal
      onHide={props.onClose}
      show={props.show}
      centered={true}
      keyboard={true}
      backdrop="static"
      size="lg"
    >
      <Modal.Header closeButton>
        <Modal.Title>
          {t(tk.prLang.project)}:{' '}
          <b>
            {props.project?.name}
            {props.project?.number ? ` (${props.project?.number})` : ''}
          </b>
        </Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <ProjectsApplicationDetailsEditForm
          project={props.project}
          applicationType={props.applicationType}
          purchaserName={props.purchaserName}
          brandName={props.brandName}
          status={status}
          onStatusChange={setStatus}
          savedRejectedNote={props.rejectedNote}
          rejectedNote={rejectedNote}
          onRejectedNoteChange={setRejectedNote}
          readOnly={props.readOnly}
          isStatusReversible={isStatusReversible}
        />
      </Modal.Body>
      <ValidationAlert
        show={saveErrors?.length > 0}
        errors={saveErrors}
        className="m-3"
      />
      <Modal.Footer>
        {props.project?.id && (
          <LastEditorInfo
            id={props.project?.id}
            method={pok.projects.getLastEditor}
          />
        )}
        <Button variant="outline-secondary" onClick={props.onClose}>
          {t(tk.button.close)}
        </Button>
        {(!props.readOnly || isStatusReversible) && (
          <Button variant="primary" onClick={handleClick} isLoading={isLoading}>
            {t(tk.button.save)}
          </Button>
        )}
      </Modal.Footer>
    </Modal>
  );
};
